import { createSlice } from "@reduxjs/toolkit";
import {
  cancelPlacement,
  createHotspot,
  deleteHotspot,
  setCurrentFrameIndex,
  setCurrentHotspot,
  setCurrentStep,
  updateAlgorithmInput,
} from "../hotspotEditor/hotspotEditorReducers";
import { isLocalStorageAvailable } from "../../util/general";
import { steps } from "../../util/constants";
import { setCurrentlyOpenModal } from "../currentlyOpenModal/currentlyOpenModal";

export const hotspotPathStates = {
  hidden: "hidden",
  visible: "visible",
  fading: "fading",
  demo: "demo",
};

export const hotspotTabContentStates = {
  list: "list",
  card: "card",
};

export const beadStates = {
  appearing: "appearing",
  visible: "visible",
  invisible: "invisible",
};

const initialState = {
  hotspotTabContent: hotspotTabContentStates.list,
  activeHotspotDrag: false,
  dragging: false,
  overwriteConfirmationRequired: false,
  showInstructionHint: false,
  autoHint: isLocalStorageAvailable()
    ? localStorage.getItem("autoHint") !== "false"
    : true,
  hotspotPathState: hotspotPathStates.hidden,
  bead: {},
  highlightHotspotNumber: null,
  showSpinHint: true,
};

function hideHotspotPathReducer(state) {
  state.hotspotPathState = hotspotPathStates.hidden;
}
const showHotspotPathReducer = (state) => {
  state.hotspotPathState = hotspotPathStates.visible;
};
function fadeHotspotPathReducer(state) {
  if (state.hotspotPathState === hotspotPathStates.demo) {
    return;
  }
  if (state.hotspotPathState === hotspotPathStates.visible) {
    state.hotspotPathState = hotspotPathStates.fading;
  }
}

const openInstructionHintReducer = (state) => {
  state.showInstructionHint = true;
  state.showSpinHint = true;
};
const closeInstructionHintReducer = (state) => {
  state.showInstructionHint = false;
  state.showSpinHint = false;
};
const handleSetCurrentStep = (state, action) => {
  const step = action.payload;
  if (step === steps.preview) {
    state.hotspotTabContent = hotspotTabContentStates.list;
    closeInstructionHintReducer(state);
  } else if (step === steps.advanced) {
    state.hotspotTabContent = hotspotTabContentStates.list;
  } else if (step === steps.configureLocations) {
    if (getAutoHint(state)) {
      openInstructionHintReducer(state);
    }
  }
};
const handleSetCurrentHotspot = (state, action) => {
  const hotspotIndex = action.payload;
  if (hotspotIndex === null) {
    state.hotspotTabContent = hotspotTabContentStates.list;
  }
};

const resetBeadStateReducer = (state) => {
  state.bead = {};
};

export const uiSlice = createSlice({
  name: "ui",
  initialState,
  reducers: {
    selectHotspot(state, action) {
      const { hotspotIndex } = action.payload;
      if (hotspotIndex === null) {
        state.hotspotTabContent = hotspotTabContentStates.list;
      } else {
        state.hotspotTabContent = hotspotTabContentStates.card;
      }
      state.highlightHotspotNumber = null;
    },
    doneWithLocations(state) {
      state.hotspotTabContent = hotspotTabContentStates.card;
      closeInstructionHintReducer(state);
      hideHotspotPathReducer(state);
    },
    setDragging(state, action) {
      state.dragging = action.payload;
    },
    setOverwriteConfirmationRequired(state, action) {
      state.overwriteConfirmationRequired = action.payload;
    },
    dragActiveHotspot(state) {
      showHotspotPathReducer(state);
      state.activeHotspotDrag = true;
      state.dragging = true;
    },
    releaseActiveHotspotDrag(state) {
      fadeHotspotPathReducer(state);
      state.activeHotspotDrag = false;
      state.dragging = false;
    },
    setBeadState(state, action) {
      const { frameArray, value } = action.payload;
      for (const frame of frameArray) {
        state.bead[frame] = value;
      }
    },
    setHighlightHotspotNumber(state, action) {
      state.highlightHotspotNumber = action.payload;
    },
    setAutoHint(state, action) {
      const { payload } = action;
      state.autoHint = payload;
      isLocalStorageAvailable() &&
        localStorage.setItem("autoHint", action.payload);
      const { openInstructionHint, closeInstructionHint } =
        uiSlice.caseReducers;
      if (payload) {
        openInstructionHint(state);
      } else {
        closeInstructionHint(state);
      }
    },
    resetBeadState: resetBeadStateReducer,
    openInstructionHint: openInstructionHintReducer,
    closeInstructionHint: closeInstructionHintReducer,
    showHotspotPath: showHotspotPathReducer,
    hideHotspotPath: hideHotspotPathReducer,
    fadeHotspotPath: fadeHotspotPathReducer,
    demoHotspotPath: (state) => {
      state.hotspotPathState = hotspotPathStates.demo;
    },
    showSpinHint(state) {
      state.showSpinHint = true;
    },
    hideSpinHint(state) {
      state.showSpinHint = false;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(updateAlgorithmInput, closeInstructionHintReducer);
    builder.addCase(setCurrentStep, handleSetCurrentStep);
    builder.addCase(setCurrentHotspot, handleSetCurrentHotspot);
    builder.addCase(deleteHotspot, (state) => {
      state.hotspotTabContent = hotspotTabContentStates.list;
    });
    builder.addCase(createHotspot, (state) => {
      state.hotspotTabContent = hotspotTabContentStates.list;
    });
    builder.addCase(cancelPlacement, closeInstructionHintReducer);
    builder.addCase(setCurrentlyOpenModal, closeInstructionHintReducer);
    builder.addCase(setCurrentFrameIndex, uiSlice.caseReducers.hideSpinHint);
  },
});

export const {
  selectHotspot,
  doneWithLocations,
  setDragging,
  setOverwriteConfirmationRequired,
  dragActiveHotspot,
  releaseActiveHotspotDrag,
  setBeadState,
  setHighlightHotspotNumber,
  setAutoHint,
  resetBeadState,
  openInstructionHint,
  closeInstructionHint,
  showHotspotPath,
  hideHotspotPath,
  fadeHotspotPath,
  demoHotspotPath,
  showSpinHint,
  hideSpinHint,
} = uiSlice.actions;
export default uiSlice.reducer;

//========== SELECTORS ============
export const getHotspotTabContent = (state) => state.hotspotTabContent;
export const getActiveHotspotDrag = (state) => state.activeHotspotDrag;
export const getDragging = (state) => state.dragging;
export const getOverwriteConfirmationRequired = (state) =>
  state.overwriteConfirmationRequired;
export const getShowInstructionHint = (state) => state.showInstructionHint;
export const getAutoHint = (state) => state.autoHint;
export const getHotspotPathState = (state) => state.hotspotPathState;
export const getBeadState = (state, { frame }) => state.bead[frame];
export const getHighlightHotspotNumber = (state) =>
  state.highlightHotspotNumber;
export const getShowSpinHint = (state) => state.showSpinHint;
